import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { zonaTactil } from '#components/mobile/ui/tactil';
import { color, radio, texto } from '#components/mobile/ui/tokens';

/**
 * La «i» discreta del pie de Ajustes, junto a la versión: al tocarla abre
 * una hoja con las licencias. Normalmente no se ve nada más que la «i».
 */
export function BotonLicencias() {
  const { t } = useTranslation();
  const [abierta, setAbierta] = useState(false);
  return (
    <>
      <Button
        variant="bare"
        aria-label={t('Licenses')}
        data-testid="licencias-abrir"
        onPress={() => setAbierta(true)}
        className={css(zonaTactil())}
        style={{
          width: 22,
          height: 22,
          padding: 0,
          borderRadius: radio.pildora,
          color: color.fg3,
        }}
      >
        <Icono nombre="info" size={15} />
      </Button>
      {abierta && <HojaLicencias onCerrar={() => setAbierta(false)} />}
    </>
  );
}

function HojaLicencias({ onCerrar }: { onCerrar: () => void }) {
  const { t } = useTranslation();
  return (
    <Hoja
      aria-label={t('Licenses')}
      data-testid="hoja-licencias"
      onCerrar={onCerrar}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Text
          role="heading"
          aria-level={2}
          style={{ ...texto.titulo, color: color.fg }}
        >
          <Trans>Licenses</Trans>
        </Text>
        <Button
          variant="bare"
          aria-label={t('Close')}
          data-testid="hoja-cerrar"
          onPress={onCerrar}
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: color.surface2,
            color: color.fg,
          }}
        >
          <Icono nombre="x" size={16} />
        </Button>
      </View>
      <LicenciasSettings />
    </Hoja>
  );
}

/**
 * Contenido de «Licencias» (la «i» del pie de Ajustes): el copyright de Rumbo y el aviso MIT de Actual
 * Budget, que la licencia MIT obliga a conservar. El aviso va en inglés, tal
 * cual: es un texto legal y no se traduce. Un test comprueba que coincide
 * letra por letra con el de `LICENSE.txt`.
 */
export function LicenciasSettings() {
  return (
    <View style={{ gap: 12 }} data-testid="licencias">
      <View style={{ gap: 4 }}>
        <Text style={{ ...texto.fila, color: color.fg }}>
          <Trans>Rumbo © 2026 Vadym Romayev Romayeva</Trans>
        </Text>
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Trans>
            Rumbo uses free software made by other people, each part with its
            own license. Below is the notice for Actual Budget, the project
            Rumbo is built on.
          </Trans>
        </Text>
      </View>
      <View
        style={{
          gap: 8,
          padding: 12,
          borderRadius: radio.sm,
          backgroundColor: color.surface2,
        }}
      >
        <Text style={{ ...texto.fila, fontSize: 14, color: color.fg }}>
          {NOMBRE_ACTUAL}
        </Text>
        <Text
          lang="en"
          data-testid="licencias-mit"
          style={{
            ...texto.secundario,
            fontSize: 12,
            color: color.fg2,
            whiteSpace: 'pre-wrap',
            userSelect: 'text',
          }}
        >
          {AVISO_MIT_ACTUAL}
        </Text>
      </View>
    </View>
  );
}

const NOMBRE_ACTUAL = 'Actual Budget';

/** Aviso MIT de Actual Budget, idéntico al de `LICENSE.txt` (no se traduce). */
export const AVISO_MIT_ACTUAL = `Copyright James Long

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`;
